import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Form, Upload, message, Modal, Input, Select, Progress, Descriptions, Spin } from 'antd';
import { UploadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../api/axios';

const OTAManagement = () => {
  const [otaUpdates, setOtaUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);

  // 获取OTA更新列表
  const fetchOtaUpdates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/devices/ota/updates');
      setOtaUpdates(response.data);
    } catch (error) {
      message.error('获取OTA更新列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchOtaUpdates();
  }, []);

  // 处理文件上传
  const handleFileUpload = async (file) => {
    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/devices/ota/updates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadedFileInfo(response.data);
      setFileList([{
        uid: Date.now().toString(),
        name: file.name,
        status: 'done',
        url: response.data.filePath,
      }]);
      message.success('文件上传成功');
    } catch (error) {
      message.error('文件上传失败: ' + (error.response?.data?.message || error.message));
      setFileList([]);
    } finally {
      setFileUploading(false);
    }
    return false; // 阻止自动上传
  };

  // 处理表单提交
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        version: values.version,
        description: values.description,
        filePath: uploadedFileInfo?.filePath || selectedUpdate?.file_path,
        fileSize: uploadedFileInfo?.fileSize || selectedUpdate?.file_size,
        md5: uploadedFileInfo?.md5 || selectedUpdate?.md5,
      };

      let response;
      if (selectedUpdate) {
        // 更新
        response = await api.put(`/admin/devices/ota/updates/${selectedUpdate.id}`, data);
        message.success('OTA更新编辑成功');
      } else {
        // 创建
        response = await api.post('/admin/devices/ota/updates', data);
        message.success('OTA更新创建成功');
      }

      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      setUploadedFileInfo(null);
      setSelectedUpdate(null);
      fetchOtaUpdates();
    } catch (error) {
      message.error(selectedUpdate ? '编辑OTA更新失败' : '创建OTA更新失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理删除更新
  const handleDeleteUpdate = async (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个OTA更新吗？删除后相关的固件文件也会被删除。',
      onOk: async () => {
        setLoading(true);
        try {
          await api.delete(`/admin/devices/ota/updates/${id}`);
          message.success('OTA更新已删除');
          fetchOtaUpdates();
        } catch (error) {
          message.error('删除OTA更新失败');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 处理编辑更新
  const handleEditUpdate = (update) => {
    setSelectedUpdate(update);
    form.setFieldsValue({
      version: update.version,
      description: update.description
    });
    // 设置已有的文件信息
    if (update.file_path) {
      setFileList([{
        uid: update.id.toString(),
        name: `已上传文件 (${(update.file_size / 1024 / 1024).toFixed(2)} MB)`,
        status: 'done',
        url: update.file_path,
      }]);
      setUploadedFileInfo({
        filePath: update.file_path,
        fileSize: update.file_size,
        md5: update.md5
      });
    }
    setModalVisible(true);
  };

  // 打开创建模态框
  const handleCreateUpdate = () => {
    setSelectedUpdate(null);
    setFileList([]);
    setUploadedFileInfo(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 表格列配置
  const columns = [
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size) => size ? `${(size / 1024 / 1024).toFixed(2)} MB` : '未知',
    },
    {
      title: 'MD5',
      dataIndex: 'md5',
      key: 'md5',
      render: (md5) => md5 ? <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{md5.substring(0, 16)}...</span> : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <div>
          <Button icon={<EditOutlined />} size="small" style={{ marginRight: 8 }} onClick={() => handleEditUpdate(record)}>
            编辑
          </Button>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteUpdate(record.id)}>
            删除
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Card title="OTA更新管理" extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreateUpdate}>创建更新</Button>}>
        <Button icon={<ReloadOutlined />} onClick={fetchOtaUpdates} style={{ marginBottom: 16 }} loading={loading}>
          刷新列表
        </Button>
        <Table 
          columns={columns} 
          dataSource={otaUpdates} 
          rowKey="id" 
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* 创建/编辑OTA更新模态框 */}
      <Modal
        title={selectedUpdate ? "编辑OTA更新" : "创建OTA更新"}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          setSelectedUpdate(null);
          form.resetFields();
          setFileList([]);
          setUploadedFileInfo(null);
        }}
        okText="确定"
        cancelText="取消"
        confirmLoading={loading || fileUploading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="version"
            label="版本号"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="请输入版本号，如 1.0.0" />
          </Form.Item>

          <Form.Item
            name="description"
            label="更新描述"
            rules={[{ required: true, message: '请输入更新描述' }]}
          >
            <Input.TextArea rows={4} placeholder="请输入更新描述" />
          </Form.Item>

          <Form.Item
            label="上传固件包"
            rules={selectedUpdate ? [] : [{ required: true, message: '请上传固件包' }]}
          >
            <Upload
              name="file"
              fileList={fileList}
              beforeUpload={handleFileUpload}
              maxCount={1}
              accept=".zip,.apk"
              fileListRender={(fileList) => (
                fileList.map(file => (
                  <div key={file.uid} style={{ padding: '8px 0' }}>
                    {file.status === 'done' ? (
                      <span>✅ {file.name}</span>
                    ) : file.status === 'uploading' ? (
                      <span><Spin size="small" /> {file.name}</span>
                    ) : (
                      <span>{file.name}</span>
                    )}
                  </div>
                ))
              )}
            >
              <Button icon={<UploadOutlined />} loading={fileUploading}>
                {selectedUpdate ? '重新上传固件包' : '选择文件'}
              </Button>
            </Upload>
            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              支持 .zip 和 .apk 格式，文件大小不超过 500MB
              {selectedUpdate && <span style={{ color: '#999' }}>（可选，不修改可不上传）</span>}
            </div>
            {uploadedFileInfo?.md5 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#666', fontFamily: 'monospace' }}>
                MD5: {uploadedFileInfo.md5}
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OTAManagement;
